import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { Lightbulb, CircleNotch, ChatCircleDots, CheckCircle, Warning, ChartBar, Robot } from "@phosphor-icons/react";

const fmt = (n) => (n || 0).toLocaleString("sv-SE");

function timeAgo(iso) {
  if (!iso) return "—";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "nyss";
  if (s < 3600) return `${Math.floor(s / 60)} min sedan`;
  if (s < 86400) return `${Math.floor(s / 3600)} h sedan`;
  return `${Math.floor(s / 86400)} d sedan`;
}

function StatCard({ icon: Icon, label, value, sub, testid, accent }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-zinc-400">
        <Icon size={16} weight="fill" style={{ color: accent || "#0033FF" }} /> {label}
      </div>
      <div className="mt-2 font-display font-extrabold text-2xl tracking-tight">{value}</div>
      {sub && <div className="mt-1 text-xs text-zinc-400">{sub}</div>}
    </div>
  );
}

export default function KnowledgeGaps() {
  const [data, setData] = useState(null);
  const [agents, setAgents] = useState([]);
  const [agentId, setAgentId] = useState("");
  const [days, setDays] = useState(30);

  useEffect(() => { api.get("/agents").then((r) => setAgents(r.data || [])).catch(() => {}); }, []);

  const load = useCallback(() => {
    setData(null);
    api.get("/kb-gaps", { params: { agent_id: agentId, days } })
      .then((r) => setData(r.data)).catch(() => setData(false));
  }, [agentId, days]);
  useEffect(() => { load(); }, [load]);

  const gapRate = data && data.total ? Math.round((data.unanswered / data.total) * 100) : 0;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="knowledge-gaps-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display font-extrabold text-2xl flex items-center gap-2">
            <Lightbulb size={26} weight="fill" className="text-[#0033FF]" /> Kunskapsluckor
          </h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-2xl">Här ser du vad dina handläggare frågar kunskapsassistenten i Senzum Chatt – de vanligaste frågorna och de som assistenten <strong>inte kunde svara på</strong>. Fyll på kunskapsbasen där behovet är störst.</p>
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="gaps-agent-filter" value={agentId} onChange={(e) => setAgentId(e.target.value)}
            className="rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0033FF]/30">
            <option value="">Alla agenter</option>
            {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <select data-testid="gaps-period-filter" value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0033FF]/30">
            <option value={7}>7 dagar</option>
            <option value={30}>30 dagar</option>
            <option value={90}>90 dagar</option>
          </select>
        </div>
      </div>

      {data === null ? (
        <div className="p-20 flex justify-center"><CircleNotch size={28} className="animate-spin text-[#0033FF]" /></div>
      ) : data === false ? (
        <div className="p-10 text-center text-zinc-500" data-testid="gaps-error">Kunde inte ladda data.</div>
      ) : data.total === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-xl py-16 text-center" data-testid="gaps-empty">
          <div className="mx-auto h-12 w-12 rounded-xl bg-[#0033FF]/10 flex items-center justify-center">
            <Lightbulb size={24} weight="duotone" className="text-[#0033FF]" />
          </div>
          <h3 className="mt-4 font-display font-bold text-lg">Inga frågor än</h3>
          <p className="mt-1 text-sm text-zinc-500 max-w-sm mx-auto">När dina handläggare använder "Fråga kunskapsbasen" i Senzum Chatt dyker deras frågor upp här – och du ser direkt vilka kunskapsluckor som behöver fyllas.</p>
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={ChatCircleDots} label="Frågor totalt" value={fmt(data.total)} sub="Under perioden" testid="stat-total-questions" />
            <StatCard icon={CheckCircle} label="Besvarade" value={fmt(data.answered)} sub="Hittades i kunskapsbasen" testid="stat-answered" accent="#16a34a" />
            <StatCard icon={Warning} label="Kunskapsluckor" value={fmt(data.unanswered)} sub={`${gapRate}% av frågorna`} testid="stat-unanswered" accent="#dc2626" />
            <StatCard icon={ChartBar} label="Unika frågor" value={fmt(data.unique_questions)} sub="Olika formuleringar" testid="stat-unique" accent="#7c3aed" />
          </div>

          {/* knowledge gaps — questions the assistant couldn't answer */}
          <div className="bg-white border border-zinc-200 rounded-xl p-6" data-testid="gaps-list">
            <h2 className="text-sm font-bold text-zinc-700 mb-1 flex items-center gap-1.5"><Warning size={16} weight="fill" className="text-red-500" /> Frågor utan svar i kunskapsbasen</h2>
            <p className="text-xs text-zinc-400 mb-4">Prioritera dessa – lägg till svaren i agentens kunskapsbas så slipper handläggarna gissa.</p>
            {data.gaps.length === 0 ? (
              <p className="text-sm text-zinc-400 py-4 text-center">🎉 Inga obesvarade frågor – kunskapsbasen täcker allt som efterfrågats.</p>
            ) : (
              <div className="space-y-2">
                {data.gaps.map((g, i) => (
                  <div key={i} data-testid={`gap-row-${i}`} className="flex items-center justify-between gap-3 rounded-lg border border-red-100 bg-red-50/50 px-3.5 py-2.5">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-zinc-800 truncate">{g.question}</div>
                      <div className="text-[11px] text-zinc-400 flex items-center gap-1.5"><Robot size={11} weight="fill" /> {g.agent_name || "Agent"} · senast {timeAgo(g.last_ts)}</div>
                    </div>
                    <span className="shrink-0 text-xs font-bold text-red-700 bg-red-100 rounded-full px-2.5 py-1">{g.gap}× utan svar</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* most-asked questions */}
          <div className="bg-white border border-zinc-200 rounded-xl p-6" data-testid="top-questions">
            <h2 className="text-sm font-bold text-zinc-700 mb-4 flex items-center gap-1.5"><ChatCircleDots size={16} weight="fill" className="text-[#0033FF]" /> Vanligaste frågorna</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-zinc-400 border-b border-zinc-100">
                    <th className="py-2 pr-4 font-bold">Fråga</th>
                    <th className="py-2 pr-4 font-bold">Agent</th>
                    <th className="py-2 pr-4 font-bold text-right">Antal</th>
                    <th className="py-2 pr-4 font-bold text-right">Utan svar</th>
                    <th className="py-2 font-bold text-right">Senast</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_questions.map((q, i) => (
                    <tr key={i} data-testid={`top-q-row-${i}`} className="border-b border-zinc-50 hover:bg-zinc-50/60">
                      <td className="py-3 pr-4 font-medium text-zinc-800 max-w-md truncate">{q.question}</td>
                      <td className="py-3 pr-4 text-zinc-500 text-xs">{q.agent_name || "—"}</td>
                      <td className="py-3 pr-4 text-right font-semibold">{fmt(q.count)}</td>
                      <td className="py-3 pr-4 text-right">
                        {q.gap > 0 ? <span className="text-xs font-bold text-red-700 bg-red-100 rounded px-1.5 py-0.5">{q.gap}</span> : <span className="text-zinc-300">—</span>}
                      </td>
                      <td className="py-3 text-right text-xs text-zinc-400">{timeAgo(q.last_ts)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
